import React, { useEffect, useMemo } from 'react';
import { useIsMobile } from '@dapp/multi-lib/ui/shared-hooks/useIsMobile';
import { useToast } from '@dapp/multi-lib/ui/toast';
import { ModalV2 } from '@dapp/multi-lib/ui/V2Modal';
import { getNetworkFromChainId } from '@dapp/multi-lib/network';
import Image from 'next/image';
import { useTranslation } from 'react-i18next';
import { Input } from './../components/form/Input';
import { FormControl } from './../components/form/FormControl';
import Paragraph from 'structure/launchpad/components/Typography';
import { Button } from '@dapp/multi-lib/sdaoui/Button';
import { InputTokenAmount } from 'structure/staking/components/form/InputTokenAmount';
import { StakeDetails } from 'structure/staking/components/StakeDetails';
import {
  useAddressStakingInfo,
  useEarlyUnlockFeePerDay,
  useWithdraw,
} from 'structure/staking/hooks/useStakingContract';
import { waitForTransaction } from 'utils/waitForTransaction';
import {
  useStakingPoolData,
  useStakingPoolList,
  useStakingUserPoolInfo,
  useStakingWithdrawCalcInfo,
  useWalletSREP,
} from 'structure/staking/hooks/useStakingAPI';
import { formatUnits, parseUnits } from 'utils/units';
import { WithdrawDetails } from 'structure/staking/components/WithdrawDetails';
import { DetailsRow } from 'structure/staking/components/DetailRow';
import { createWithdrawValidation } from 'structure/staking/utils/createValidationSchema';
import { useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useBlock, useConnection } from 'wagmi';
import { useParams } from 'next/navigation';
import { Body2, Title4, Title5 } from 'components/Typography';
import { ethers } from 'ethers';
import { getTxErrorMessage } from 'utils/getTxErrorMessage';
import { getStaticAssetUrl } from 'utils/url';
import { StakingPool } from 'api/StakingAPI';
import { calculateStakingWithdrawalQuote } from 'structure/staking/utils/stakingWithdrawal';

type DepositModalProps = {
  open: boolean;
  poolId: number;
  onOpenChange: (isOpen: boolean) => void;
};

type FormData = {
  tokenAmount?: string;
};

export function ModalStakeWithdraw({
  open,
  poolId,
  onOpenChange,
}: DepositModalProps) {
  const { t } = useTranslation('common');
  const isMobile = useIsMobile(834);
  const toastV2 = useToast();
  const account = useConnection();
  const params = useParams<{ id: string }>();

  const { data = [] } = useStakingPoolList({}, params?.id ?? '');
  const initialPool = data.find((pool) => pool.id === poolId);
  const stakingData = useStakingPoolData(initialPool as StakingPool);
  const pool = stakingData?.data;
  const networkInfo = getNetworkFromChainId(pool.chainId);

  const { data: stakingInfo = [] } = useAddressStakingInfo({
    contractAddress: pool.stakingContractAddress,
    address: account.address as `0x${string}`,
    enabled: open,
  });

  const stakingBalance = formatUnits(
    stakingInfo[0] || 0,
    pool.depositTokenDecimals
  );

  const schema = createWithdrawValidation({
    tokenAmount: {
      label: t('STAKING.TOKEN_AMOUNT'),
      max: stakingBalance,
      min: ethers.utils.formatUnits(1, pool.depositTokenDecimals),
      decimals: pool.depositTokenDecimals,
      required: true,
    },
  });

  const form = useForm<FormData>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: { tokenAmount: '' },
  });

  const watch = useWatch({ control: form.control });

  const bnTokenAmount = parseUnits(
    form.getFieldState('tokenAmount').invalid
      ? 0
      : form.getValues('tokenAmount') ?? 0,
    pool.depositTokenDecimals
  );

  const handleClickMax = () => {
    form.setValue('tokenAmount', stakingBalance);
    form.trigger('tokenAmount');
  };

  const withdraw = useWithdraw({
    contractAddress: pool.stakingContractAddress,
    tokenAmount: bnTokenAmount.toString(),
    enabled: open && form.formState.isValid,
  });

  const handleOpenChange = (isOpen: boolean) => {
    onOpenChange(isOpen);
  };

  const stakingCalculation = useStakingWithdrawCalcInfo({
    poolContractAddress: pool.stakingContractAddress,
    walletAddress: account.address as `0x${string}`,
    chainId: pool.chainId,
    tokenAmount: bnTokenAmount.toString(),
    query: {
      enabled: open && form.formState.isValid,
    },
  });

  const walletSREP = useWalletSREP({
    walletAddress: account.address as `0x${string}`,
    query: {
      enabled: !!account.address,
    },
  });

  const userPoolInfo = useStakingUserPoolInfo({
    poolContractAddress: pool.stakingContractAddress,
    walletAddress: account.address as `0x${string}`,
    chainId: pool.chainId,
    pool,
  });

  const { data: currentBlock, isLoading: isBlockLoading } = useBlock({
    chainId: pool.chainId,
    watch: true,
    query: { enabled: open },
  });
  const earlyUnlockFeePerDay = useEarlyUnlockFeePerDay({
    contractAddress: pool.stakingContractAddress,
    chainId: pool.chainId,
    enabled: open,
  });

  const handleWithdraw = async () => {
    if (withdraw.prepare.isError) {
      toastV2({
        title: withdraw.prepare.error.message,
        description: t('STAKING.FAILED_TO_REQUEST_WITHDRAW'),
        type: 'error',
      });
      return;
    }

    try {
      const result = await withdraw.contract.writeContractAsync(
        withdraw.prepare.data!.request
      );

      await waitForTransaction({
        chainId: pool.chainId,
        hash: result,
        connectorId: account.connector?.id,
      });

      toastV2({
        title: t('STAKING.WITHDRAW_SUCCESS'),
        description: `Code: ${result}`,
        type: 'success',
      });

      await walletSREP.refetch();
      handleOpenChange(false);
    } catch (error) {
      const message = getTxErrorMessage(
        error as Error,
        t('common:ERROR_MESSAGES.UNKNOWN_ERROR')
      );

      console.log('### withdraw', withdraw);
      console.log('### error', error);
      toastV2({
        title: t('ERROR_MESSAGES.ERROR'),
        description: message,
        type: 'error',
      });
    }
  };

  const handleSubmit = async () => {
    await handleWithdraw();
  };

  const withdrawalQuote = useMemo(
    () =>
      calculateStakingWithdrawalQuote({
        amount: bnTokenAmount.toString(),
        claimable: userPoolInfo.data?.claimable ?? '0',
        unlockTimestamp: userPoolInfo.data?.unlockDate ?? '0',
        currentTimestamp: currentBlock?.timestamp
          ? Number(currentBlock.timestamp)
          : Math.floor(Date.now() / 1000),
        earlyUnlockFeePerDay: earlyUnlockFeePerDay.data?.toString() ?? '0',
      }),
    [
      bnTokenAmount,
      currentBlock?.timestamp,
      earlyUnlockFeePerDay.data,
      userPoolInfo.data?.claimable,
      userPoolInfo.data?.unlockDate,
    ]
  );
  const hasLiveWithdrawalData =
    stakingData.isFetched &&
    userPoolInfo.isFetched &&
    !!currentBlock &&
    earlyUnlockFeePerDay.data !== undefined &&
    !earlyUnlockFeePerDay.isError;
  const isLiveQuoteLoading =
    !hasLiveWithdrawalData ||
    isBlockLoading ||
    earlyUnlockFeePerDay.isLoading ||
    userPoolInfo.isLoading;
  const isCalculationLoading =
    stakingCalculation.isFetching || isLiveQuoteLoading;
  const stakingCalcData = stakingCalculation.data
    ? {
        ...stakingCalculation.data,
        daysLeft: withdrawalQuote.daysLeft,
        penalty: withdrawalQuote.penalty,
        netReceivable: withdrawalQuote.principalReceivable,
        withdrawFeePercentage: withdrawalQuote.withdrawFeePercentage,
      }
    : undefined;
  const formattedPenalty = formatUnits(
    withdrawalQuote.penalty,
    pool.depositTokenDecimals
  );
  const principalReceivable = formatUnits(
    withdrawalQuote.principalReceivable,
    pool.depositTokenDecimals
  );
  const rewardsClaimed = formatUnits(
    withdrawalQuote.rewardsClaimed,
    pool.depositTokenDecimals
  );
  const totalReceivable = formatUnits(
    withdrawalQuote.totalReceivable,
    pool.depositTokenDecimals
  );
  const hasPenalty = hasLiveWithdrawalData && withdrawalQuote.penalty !== '0';

  useEffect(() => {
    if (Number(watch.tokenAmount) > 0) {
      form.trigger('tokenAmount');
    }
  }, [watch]);

  return (
    <ModalV2.Root open={open} onOpenChange={handleOpenChange}>
      <ModalV2.Content
        variant='dark'
        fullscreen={isMobile}
        scrollable
        onInteractOutside={(event) => {
          event.preventDefault();
        }}
      >
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <div className='flex flex-row items-center justify-between'>
            <ModalV2.Title variant='dark'>
              <Title4>{t('STAKING.MODAL_WITHDRAW_TITLE')}</Title4>
            </ModalV2.Title>
            <ModalV2.Close variant='dark' />
          </div>
          <ModalV2.BodyTitle variant='dark'>
            <Title5>
              {t('STAKING.AVAILABLE')} {pool.depositTokenSymbol}
            </Title5>
          </ModalV2.BodyTitle>
          <ModalV2.Body variant='dark' className='mb-5'>
            <FormControl
              label={
                <div className='flex flex-row'>
                  <Paragraph className='text-sm text-[#B4BCD0]'>
                    {t('STAKING.NETWORK')}
                  </Paragraph>
                </div>
              }
            >
              <Input
                value=''
                type='text'
                disabled
                startEnhancer={
                  <div className='flex items-center gap-2'>
                    <Image
                      alt={networkInfo.nativeCurrency.symbol}
                      src={getStaticAssetUrl(
                        `/sfi/static/image/token/${networkInfo.nativeCurrency.symbol}.svg`
                      )}
                      height={32}
                      width={32}
                    />
                    <Paragraph className='text-sm font-normal'>
                      {networkInfo.name ?? networkInfo.shortName}
                    </Paragraph>
                  </div>
                }
              />
            </FormControl>

            <InputTokenAmount
              tokenSymbol={pool.depositTokenSymbol}
              error={form.formState.errors.tokenAmount?.message}
              onMax={handleClickMax}
              {...form.register('tokenAmount')}
              label={
                <div className='flex flex-row justify-end'>
                  <Paragraph className='text-xs	text-[#B4BCD0]'>
                    {t('STAKING.BALANCE')} :{' '}
                    {t('tokenAmount', { value: stakingBalance })}{' '}
                    {pool.depositTokenSymbol}
                  </Paragraph>
                </div>
              }
            />
          </ModalV2.Body>

          {hasPenalty ? (
            <>
              <ModalV2.BodyTitle variant='dark' className='pb-4'>
                <Body2>{t('STAKING.WITHDRAW_TERMS')}</Body2>
              </ModalV2.BodyTitle>
              <ModalV2.Body variant='dark'>
                <WithdrawDetails
                  pool={pool}
                  loading={isCalculationLoading}
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  calculation={stakingCalcData as any}
                />
              </ModalV2.Body>
            </>
          ) : null}

          <ModalV2.BodyTitle variant='dark' className='pb-4 pt-5'>
            <Body2>{t('STAKING.WITHDRAW_NET_AMOUNT_RECEIVABLE')}</Body2>
          </ModalV2.BodyTitle>
          <ModalV2.Body variant='dark'>
            <div>
              <StakeDetails
                pool={pool}
                userPoolInfo={userPoolInfo.data}
                action='withdraw'
                loading={isCalculationLoading}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                calculation={stakingCalcData as any}
              />
            </div>
            <div className='flex flex-col gap-3'>
              {hasPenalty ? (
                <DetailsRow
                  label={t('STAKING.EARLY_WITHDRAW_PENALTY__CHARGE')}
                  labelClassName='text-[#FB5656]'
                  valueClassName='text-[#FB5656]'
                  value={`${t('intlNumber', { value: formattedPenalty })} ${pool.depositTokenSymbol}`}
                />
              ) : null}
              <DetailsRow
                label={t('STAKING.WITHDRAW_PRINCIPAL_RECEIVABLE')}
                value={`${t('intlNumber', { value: principalReceivable })} ${pool.depositTokenSymbol}`}
              />
              <DetailsRow
                label={t('STAKING.WITHDRAW_REWARDS_CLAIMED')}
                value={`${t('intlNumber', { value: rewardsClaimed })} ${pool.depositTokenSymbol}`}
              />
              <DetailsRow
                label={t('STAKING.WITHDRAW_TOTAL_RECEIVABLE')}
                labelClassName='text-sm font-semibold text-[#FFFFFF]'
                valueClassName='text-lg font-semibold text-[#FFFFFF]'
                value={`${t('intlNumber', { value: totalReceivable })} ${pool.depositTokenSymbol}`}
              />
            </div>
          </ModalV2.Body>
          <div className='mt-6 flex justify-end gap-4'>
            <Button
              onClick={() => handleOpenChange(false)}
              disabled={withdraw.contract.isPending}
              style='outline'
            >
              {t('STAKING.CANCEL')}
            </Button>

            <Button
              type='submit'
              loading={
                withdraw.contract.isPending ||
                withdraw.prepare.isLoading ||
                form.formState.isSubmitting
              }
              disabled={
                withdraw.contract.isPending ||
                withdraw.prepare.isFetching ||
                form.formState.isSubmitting ||
                isLiveQuoteLoading ||
                !form.formState.isValid
              }
            >
              {t('STAKING.WITHDRAW')}
            </Button>
          </div>
        </form>
      </ModalV2.Content>
    </ModalV2.Root>
  );
}
